// Angular
import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

// SyncFusion
import { EventHandler, getComponent } from '@syncfusion/ej2-base';
import { Dialog } from '@syncfusion/ej2-angular-popups';
import { Column, ColumnModel, Edit, EditMode, GridComponent, IEdit } from '@syncfusion/ej2-angular-grids';
import { FormValidator } from '@syncfusion/ej2-angular-inputs';
import { CheckBox } from '@syncfusion/ej2-angular-buttons';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';

// Third party
import { v4 as uuidv4 } from 'uuid';

import { FieldGroupMetadata, FieldMetadata, ViewMetadata } from '@data/cer-data-types';
import { CerDataMetaService } from '@data/cer-data-meta.service';
import { CerEditTemplateService } from '../cer-edit/cer-edit-template.service';
import { GridDesign } from './cer-grid-design';
import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum, UiKeyboardShortcut } from '@ui/cer-ui-types';
import { uiCmdEditCancel, uiCmdEditModeTooggle, uiCmdEditStart } from '@ui/cer-ui-commands';
import { CerGridSelectionService } from './cer-grid-selection.service';
import { CerDropDownList } from '../cer-control/cer-drop-down-list/cer-drop-down-list';
import { CerGridCommandService } from './cer-grid-command.service';
import { CerDataService } from '@data/cer-data.service';
import { CerPromptService } from '@ui/cer-ui-prompt.service';
import { CerGridComponent } from './cer-grid.component';

@Injectable()
export class CerGridEditService implements OnDestroy {
  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  // Grid edit
  private appGrid: CerGridComponent;
  private grid: GridComponent;
  private design: GridDesign;

  // Keyboard event
  private dialogKeyboardShortcuts: UiKeyboardShortcut[] =
    [
      { cmd: uiCmdEditCancel, code: 'Escape', alt: false, ctrl: false, shift: false, enabled: true, allowsPreventDefault: true },
      { cmd: uiCmdEditCancel, code: 'Escape', alt: false, ctrl: true, shift: false, enabled: true, allowsPreventDefault: true },
      { cmd: uiCmdEditCancel, code: 'Escape', alt: false, ctrl: false, shift: true, enabled: true, allowsPreventDefault: true }
    ];

  public init(appGrid: CerGridComponent, grid: GridComponent, design: GridDesign) {
    this.appGrid = appGrid;
    this.grid = grid;
    this.design = design;
  }

  public editedData: any;
  public editedDataOrig: any;
  public uuid: string; // Force unique id for dialog edit, making sure two saves are not successfull in the same edit
  private dialogFieldsVisibleSave: string[] = null;
  private dialogFieldsVisibleEdit: string[] = null;
  private fieldsAllowEditSave: string[] = null;
  private fieldsAllowEditEdit: string[] = null;

  public editAcceptUpdatePrimaryKey: boolean = false;

  constructor(
    private command: CerGridCommandService,
    private data: CerDataService,
    private meta: CerDataMetaService,
    private selection: CerGridSelectionService,
    private editTemplateService: CerEditTemplateService,
    private ui: CerPromptService
  ) {
    this.subscriptionManager.add(this.command.commandPre$.subscribe(event => this.onCommandPre(event)));
    this.subscriptionManager.add(this.command.commandPost$.subscribe(event => this.onCommandPost(event)));
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  // Editing
  public editModule(): Edit {
    return this.grid.editModule;
  }

  public edit(): IEdit {
    return this.editModule()?.editModule;
  }

  public getPreviousData(): any {
    return this.edit()?.previousData;
  }

  public setPreviousData(row: any) {
    const edit: IEdit = this.edit();
    this.editedDataOrig = row;
    //this.editData = row;
    if (edit != null) {
      edit.previousData = row;
    }
  }

  public mergeProperties(row: any, fromDto: Object, onlyHasValue: boolean = false): string[] {
    var propNames: string[] = [];
    if (row != null && fromDto != null) {
      Object.entries(fromDto)
        .filter(e => e.length == 2 && (onlyHasValue == false || e[1] != null) && row.hasOwnProperty(e[0]))
        .forEach(e => {
          const propName = e[0];
          const propValue = e[1];
          this.valueSet(row, propName, propValue);
          propNames.push(propName)
        });
    }
    return propNames;
  }


  public formValidator(): FormValidator {
    return this.editModule()?.formObj;
  }

  public formElement(): HTMLFormElement {
    var formElement: HTMLFormElement = this.formValidator()?.element;
    return formElement;
  }

  public editData(): any {
    var editData: any = null;
    var formElement: HTMLFormElement = this.formElement();
    if (formElement) {
      editData = this.grid.editModule.getCurrentEditedData(formElement, {});
    }
    return editData;
  }

  // Construct edit data from from one field or all fields
  // Include non visible fields?  
  public editDataGet(field: string = null): any {
    var data: any = this.editModule().getCurrentEditedData(this.formElement(), field ? field : {});
    return data;
  }

  private onCommandPre(event: UiCommandEvent) {
    var done: boolean = false;
    if (event.source == UiCommandSourceEnum.Toolbar && event.actionType == UiActionTypeEnum.ToolbarMenuItem) {
      this.batchEditToolbarClick(event.commandId);
    }
    switch (event.source) {
      case UiCommandSourceEnum.ActionBegin:
        switch (event.actionType) {
          case UiActionTypeEnum.Delete:
            this.onDeleteActionBeginPre(event);
            break;
          case UiActionTypeEnum.BeginEdit:
            this.onBeginEditActionBeginPre(event.args);
            break;
          case UiActionTypeEnum.Add:
            this.onAddActionBeginPre(event);
            break;
          case UiActionTypeEnum.Cancel:
            this.onCancelActionBeginPre(event.args);
            break;
          case UiActionTypeEnum.Save:
            this.onSaveActionBeginPre(event.args);
            break;
          case UiActionTypeEnum.BatchSave:
            this.batchEditSaveConfirmDialogReset();
            break;
        }
        break;
      case UiCommandSourceEnum.ActionComplete:
        switch (event.actionType) {
          case UiActionTypeEnum.Loaded:
            this.dialogEditInit();
            this.batchEditInit();
            break;
          case UiActionTypeEnum.CellSelect:
            if (this.design.editSettings.editOnClick && event.args?.cellIndex?.rowIndex != null && event.columnName?.length > 0) {
              this.doEditStartCell(event.args.cellIndex.rowIndex, event.columnName);
            }
            break;
          case UiActionTypeEnum.BeginEdit:
            this.onBeginEditActionCompletePre(event.args);
            break;
          case UiActionTypeEnum.Add:
            this.onAddActionCompletePre(event.args);
            break;
          case UiActionTypeEnum.Save:
            this.onSaveActionCompletePre(event.args);
            break;
          case UiActionTypeEnum.Cancel:
            this.onCancelActionCompletePre(event.args);
            //event.args.cancel = true; // avoid further keyboard cancel
            break;
        }
        break;
      default:
        switch (event.commandId) {
          case uiCmdEditStart.id:
            if (this.canEditStart()) {
              done = this.doEditStart();
            }
            break;
          case uiCmdEditCancel.id:
            done = this.onCmdEditCancel(event);
            break;
          case uiCmdEditModeTooggle.id:
            done = this.doEditModeToggle();
            break;
        }
        break;
    }

    if (done) {
      event.args.cancel = true;
    }
  }

  private onCommandPost(cmd: UiCommandEvent) {
    if (cmd.source == UiCommandSourceEnum.ActionBegin) {
      switch (cmd.actionType) {
        case UiActionTypeEnum.BeginEdit:
          this.onBeginEditActionBeginPost(cmd.args);
          break;
        case UiActionTypeEnum.Add:
          this.onAddActionBeginPost(cmd.args);
          break;
      }
    }
  }

  public allowDelete(allowDelete: boolean) {
    if (this.grid?.editSettings) {
      this.grid.editSettings.allowDeleting = allowDelete;
    }
  }

  public editMode(editMode: EditMode) {
    if (this.grid?.editSettings) {
      this.grid.editSettings.mode = editMode;
    }
  }

  public canEdit(): boolean {
    return this.grid?.editSettings?.allowEditing;
  }

  public canAdd(): boolean {
    return this.grid?.editSettings?.allowAdding;
  }

  public allowEdit(allowEdit: boolean) {
    if (this.grid?.editSettings) {
      this.grid.editSettings.allowEditing = allowEdit;
    }
  }

  public allowEditField(fieldName: string, allowEdit: boolean) {
    const column = this.grid?.getColumnByField(fieldName);
    if (column) {
      column.allowEditing = allowEdit;
    }
  }

  public allowAdd(allowAdd: boolean) {
    if (this.grid?.editSettings) {
      this.grid.editSettings.allowAdding = allowAdd;
    }
  }

  // Loaded
  private containerDiv: ElementRef;
  public dialogEditInit(containerDiv: ElementRef = null) {
    if (containerDiv != null) {
      this.containerDiv = containerDiv;
    }
    const editDlg = this.design?.editSettings?.dialog;
    if (editDlg?.editInPane === true) {
      if (this.containerDiv != null) {
        const editModule = this.grid?.editModule?.editModule;
        if (editModule != null) {
          if (this.grid.editSettings.dialog.params.target == 'containerDiv') {
            this.grid.editSettings.dialog.params.target = this.containerDiv.nativeElement;
          }
          const renderer = (editModule as any).renderer;
          if (renderer != null) {
            renderer.dialogRendererUpdateOrig = renderer.update;
            renderer.targetCer = this.containerDiv.nativeElement;
            renderer.update = this.dialogRendererUpdate;
          }
        }
      }
    }
  }

  public dialogRendererUpdate(elements: any, args: any) {
    const rAny = (this as any);  // this = GridEditRender type
    if (rAny.targetCer != null) {
      args ??= {};
      args.target = rAny.targetCer;
    }
    rAny.dialogRendererUpdateOrig(elements, args);
    if (rAny.targetCer != null) {
      //    if (this.dataMeta.viewMetadata.dialogHeight == '100%') {
      if (rAny.renderer?.dialogObj.element != null) {
        rAny.renderer.dialogObj.element.style.maxHeight = '100%';
      }
    }
  }

  // Begin delete
  private deleteConfirmed: boolean = false
  private onDeleteActionBeginPre(e: UiCommandEvent) {
    if (!this.deleteConfirmed) {
      if (this.design?.editSettings?.deleteConfirm !== false) {
        var edit: Edit = this.grid?.editModule;
        if (edit) {
          var content: string = '';
          if (e.args.data?.length > 0) {
            e.args.data.forEach((data: any) => {
              var title = this.title(data, false);
              if (title?.length > 0) {
                content += '<li>' + title + '</li>';
              }
            });
          }
          if (content.length > 0) {
            content = '<ul>' + content + '</ul>';
          }
          content = this.getContentDiv(edit, 'ConfirmDelete') + content;
          this.ui.confirm(content).then(action => {
            if (action == 'ok') {
              this.deleteConfirmed = true;
              edit.deleteRecord(e.args.rowData);
            }
          });
        }
         e.cancel = true;
         e.args.cancel = true;
      }
    }
    this.deleteConfirmed = false;
  }

  private getContentH3(edit: Edit, id: string): string {
    var contentDiv = '<h3>' + (<any>edit).l10n?.getConstant(id) + '</h2>'
    return contentDiv;
  }

  private getContentDiv(edit: Edit, id: string): string {
    var contentDiv = '<div>' + (<any>edit).l10n?.getConstant(id) + '</div>'
    return contentDiv;
  }

  private showDialog(content: string, dialog: any) {
    if (dialog) {
      dialog.content = content;
      dialog.dataBind();
      dialog.show();
    }
  }

  // Begin edit
  private onBeginEditActionBeginPre(e: UiCommandEvent) {
    if (document.activeElement && document.activeElement.tagName == "INPUT") {
      e.cancel = true;
      e.args.cancel = true; // cancel edit if input field is active (search bar etc.)
      return;
    }
    this.editInitPre(e, false);
  }

  private onBeginEditActionBeginPost(args: any) {
    this.editInitPost(args, false);
  }

  private onBeginEditActionCompletePre(args: any) {
    this.onEditAddActionCompletePre(args, false)
  }

  // Add
  private onAddActionBeginPre(e: UiCommandEvent) {
    if (!this.appGrid.isRendered()) {
      this.ui.toast("Data er under indlæsning. Afvent venligst.","Ny post");
      e.cancel = true;
      return;
    }

    this.editInitPre(e, true);
  }

  private onAddActionCompletePre(args: any) {
    this.editedDataOrig = args.rowData;
    this.onEditAddActionCompletePre(args, true);
  }

  private onAddActionBeginPost(args: any) {
    this.editInitPost(args, true);
  }

  // Edit+Add
  private onEditAddActionCompletePre(args: any, isAdd: boolean) {
    if (args.form) {
      args.form.autocomplete = 'off';
    }

    this.editedData = args.rowData;
    this.dialogInit(args.dialog, args.rowData, isAdd);
    this.fieldGroupSetup(args.dialog);
  }

  // On Cancel
  private onCancelActionBeginPre(args: any) {
    this.dialogFieldsResetVisible();
    this.fieldsResetAllowEdit();
  }

  private onCancelActionCompletePre(args: any) {
    this.resetEditOriginalData();
  }

  public canEditStart(): boolean {
    if (!this.canEdit()) {
      return false;
    }
    var ok: boolean = true;
    if (this.grid.filterModule) {
      var m: any = this.grid.filterModule;
      if (m.fltrDlgDetails != null) {
        ok = !m.fltrDlgDetails.isOpen;
      }
    }
    return ok;
  }

  public doEditStartCell(rowIndex: number, columnName: string) {
    if (this.grid.editSettings.allowEditing && this.grid.editModule && !this.grid.isEdit) {
      this.grid.editModule.editCell(rowIndex, columnName);
    }
  }

  public doEditStart(rowIndex: number = null, columnName: string = null): boolean {
    var ok: boolean = false;

    const settings = this.grid.editSettings;
    if (settings?.allowEditing) {
      const edit = this.grid.editModule;
      if (edit) {
        if (settings.mode == 'Batch') {
          if (rowIndex != null && columnName?.length > 0) {
            edit.editCell(rowIndex, columnName);
          }
        }
        else if (!this.grid.isEdit) {
          edit.startEdit();
        }
        ok = true;

      }
    }

    return ok;
  }

  public doEditModeToggle(): boolean {
    var ok: boolean = false;
    if (this.grid && this.grid.editSettings) {
      this.selection.rowSelectedSave();
      switch (this.grid.editSettings.mode) {
        case 'Normal':
          this.grid.editSettings.mode = 'Dialog';
          this.grid.editSettings.allowNextRowEdit = false;
          break;
        case 'Dialog':
          if (this.grid.enableAdaptiveUI) {
            if (this.grid.rowRenderingMode == 'Horizontal') {
              this.grid.rowRenderingMode = 'Vertical';
              this.grid.refreshColumns();
            }
            else {
              this.grid.rowRenderingMode = 'Horizontal';
              this.grid.enableAdaptiveUI = false;
              this.grid.editSettings.mode = 'Batch';
              this.grid.editSettings.allowNextRowEdit = true;
            }
          }
          else {
            this.grid.editSettings.mode = 'Dialog';
            this.grid.enableAdaptiveUI = true;
          }
          break;
        case 'Batch':
          this.grid.editSettings.mode = 'Normal';
          this.grid.editSettings.allowNextRowEdit = false;
          break;
      }
      var infoStr = "Edit mode: " + this.grid.editSettings.mode + (this.grid.enableAdaptiveUI ? " (Adaptive " + this.grid.rowRenderingMode + ")" : "");
      this.ui.infoToast(infoStr);
      ok = true;
    }
    return ok;
  }

  // Edit handling by field name
  // Looks up input and re-directs to input methods
  public fieldValueGet(field: string): any {
    var col: Column = this.fieldColumn(field);
    var input: HTMLInputElement = this.inputUid(col?.uid);
    return this.inputValueGet(input, col, this.editedData);
  }

  public valueSet(rowData: any, field: string, value: any) {
    if (this.fieldValueSet(field, value)) {
      return true;
    }
    return this.rowFieldValueSet(rowData, field, value);
  }

  public viewMetaData(): ViewMetadata {
    return this.appGrid.viewMetadata;
  }

  public primaryKeyNameGet(): string {
    return this.viewMetaData().primaryKey as string;
  }

  public primaryKeyValue(row: any): any {
    const pkName: string = this.primaryKeyNameGet();
    return row != null && pkName?.length > 0 ? row[pkName] : null;
  }

  public valueRefreshAll(row: any, exceptFields: string[] = [], activeRow: boolean = false) {
    exceptFields ??= [];
    const allFields = this.meta.fieldMetadata
      .filter(f => f.name != this.meta.viewMetadata.primaryKey && !exceptFields.includes(f.name))
      .map(f => f.name);
    this.valueRefresh(row, allFields, activeRow);
  }

  public valueRefresh(row: any, field: string | string[], activeRow: boolean = false) {
    const fields: string[] = Array.isArray(field) ? field : [field];
    const pkValue = this.primaryKeyValue(row);
    if (pkValue || activeRow) {
      fields.forEach(f => this.grid.setCellValue(pkValue, f, row[f]));
    }
  }

  public setRowData(row: any) {
    this.grid.setRowData(this.primaryKeyValue(row), row);
  }

  public getRowsData(): any[] {
    return this.grid.contentModule.getRows() as any[];
  }

  public rowFieldValueSet(rowData: any, field: string, value: any): boolean {
    if (rowData == null || !(field?.length > 0)) {
      return false;
    }
    rowData[field] = value;
    return true;
  }

  public fieldValueSet(field: string, value: any, index: number = null): boolean {
    var input: HTMLInputElement = this.fieldInput(field,index);
    if (input) {
      this.inputValueSet(input, value);
      return true;
    }
    return false;
  }

  public fieldDisable(field: string, disable: boolean) {
    var input: HTMLInputElement = this.fieldInput(field);
    if (input) {
      this.inputDisable(input, disable);
    }
  }

  public fieldMetadata(field: string): FieldMetadata {
    return this.meta.fieldMetaGet(field);
  }

  public fieldColumn(field: string, index: number = null): Column {
    if (index == null)
    {
      return this.grid.getColumnByField(field);
    }
    else {
      const cols = this.grid.getColumns().filter(f => f.field == field);
      return  (index < cols.length) ? cols[index] : null;
    }
  }

  public fieldColumnUid(field: string, index: number = null): string {
    return (this.fieldColumn(field, index))?.uid;
  }

  // Lookup input by field name
  public fieldInput(field: string, index: number = null): HTMLInputElement {
    return this.inputUid(this.fieldColumnUid(field, index));
  }

  // Lookup input by column uid 
  public inputUid(uid: string): HTMLInputElement {
    var input: any = null;
    if (uid) {
      var form: HTMLFormElement = this.formElement();
      if (form) {
        var inputs = [].slice.call(form.getElementsByClassName('e-field'));
        for (var i: number = 0, len = inputs.length; i < len; i++) {
          var inputCheck: HTMLInputElement = inputs[i] as HTMLInputElement;
          var inputUid: string = inputCheck.getAttribute('e-mappinguid');
          if (inputUid && inputUid == uid) {
            input = inputCheck;
            break;
          }
        }
      }
    }
    return input;
  }

  // Edit handling by input
  public inputValueGet(input: HTMLInputElement, col: Column, editedData: any): any {
    if (input) {
      var value = (<any>this.editModule()).getValue(col, input, this.editedData);
      if (col.type === 'string') {
        value = this.grid.sanitize(value);
      }
      return value;
    }
    return null;
  }

  public inputDropDownListGet(input: HTMLInputElement): DropDownList {
    var ddl: DropDownList = null;
    if (input.classList.contains('e-dropdownlist')) {
      ddl = getComponent(input, 'dropdownlist');
    }
    return ddl;
  }

  public inputValueSet(input: HTMLInputElement, value: any) {
    //DataUtil.setValue(col_1.field, value, editedData);
    if (input) {
      switch (input.type) {
        case 'checkbox':
          var checkBox: CheckBox = getComponent(input, 'checkbox');
          if (checkBox) {
            checkBox.checked = value;
          }
          else {
            input.checked = value;
          }
          break;
        default:
          if (input.classList.contains('e-dropdownlist')) {
            var ddl: DropDownList = getComponent(input, 'dropdownlist');
            if (ddl) {
              ddl.value = value;
            }
          }
          else if (input.classList.contains('e-numerictextbox')) {
            var ntb: any = getComponent(input, 'numerictextbox');
            if (ntb) {
              ntb.value = value;
            }
          }
          else if (input.classList.contains('e-textbox')) {
            var tb: any = getComponent(input, 'textbox');
            if (tb) {
              tb.value = value;
            }
          }
          else {
            input.value = value;   // TODO: Formatting? (Number+Date)
          }
          if (this.inputHasFocus(input)) {
            input.blur(); // Run any validation
            this.grid?.editModule?.editFormValidate();
            input.focus();
          }
          else {
            this.validate(/*input.id*/);
          }
      }
    }
  }

  public validate(fieldName: string = null) {
    setTimeout(() => {
      if (fieldName) {
        this.grid?.editModule?.formObj.validate(fieldName);
      }
      else {
        this.grid?.editModule?.editFormValidate();
      }
    }, 1);
  }

  public inputHasFocus(input: HTMLInputElement): boolean {
    var activeElement = document.activeElement;
    return (input && activeElement === input);
  }

  public inputGetComponent(input: HTMLInputElement, type: string = null): any {
    if (input) {
      if (type?.length > 0) {
        return getComponent(input, type);
      }
      else {
        var inputAny: any = input;
        var ej2: any[] = inputAny.ej2_instances;
        if (ej2?.length > 0) {
          var component: any = ej2[0];
          return component;
        }
      }
    }
    return null;
  }

  public inputIsDisabled(input: HTMLInputElement): boolean {
    var disabled: boolean;

    if (input) {
      var done: boolean = false;
      switch (input.type) {
        case 'checkbox':
          var checkBox: CheckBox = this.inputGetComponent(input, 'checkbox');
          if (checkBox) {
            disabled = checkBox.disabled;
            done = true;
          }
          break;
        default:
          var component: any = this.inputGetComponent(input);
          if (component) {
            disabled = !component.enabled;
            done = true;
          }
      }
      if (!done) {
        var parent: HTMLElement = <HTMLElement>input.parentNode;
        disabled = (parent.classList.contains("e-disabled"));
      }
    }
    return disabled;
  }

  public inputDisable(input: HTMLInputElement, disable: boolean) {
    if (input) {
      var done: boolean = false;
      switch (input.type) {
        case 'checkbox':
          var checkBox: CheckBox = getComponent(input, 'checkbox');
          if (checkBox) {
            checkBox.disabled = disable;
          }
          else {
            input.disabled = disable;
          }
          done = true;
          break;
        default:
          var component: any = this.inputGetComponent(input);
          if (component) {
            component.enabled = !disable;
            done = true;
          }
      }
      //if (!done) {
      var parent: HTMLElement = <HTMLElement>input.parentNode;
      if (disable) {
        if (!parent.classList.contains("e-disabled")) {
          parent.classList.add("e-disabled");
        }
      }
      else {
        if (parent.classList.contains("e-disabled")) {
          parent.classList.remove("e-disabled");
        }
      }
      //}
    }
  }

  // Dialog edit + virtual scroll etc. enabled requires reset of "previousData".
  // It supplies incorrect old values for non visible dialog fields
  // Resetting will enforce reinit in NormalEdit.prototype.startEdit 
  private resetEditOriginalData() {
    (<any>this.grid.editModule.editModule).previousData = null;
  }

  private onSaveActionBeginPre(args: any) {
    this.dialogFieldsResetVisible();
    this.fieldsResetAllowEdit();

    if (args.batchChanges?.changedRecords?.length > 0) {
      const chgRows: any[] = args.batchChanges?.changedRecords?.length;
      chgRows.forEach(row => {
        row.uuid = this.uuid;
      });

    }
    else if (args.data) {
      args.data.uuid = this.uuid;  // Send dialog edit session uuid to server in the dto
    }

    if (args.action == "insert") {
      if (this.grid.pageSettings.currentPage !== 1 && this.grid.editSettings.newRowPosition === 'Top') {
        args.index = (this.grid.pageSettings.currentPage * this.grid.pageSettings.pageSize) - this.grid.pageSettings.pageSize;
      } else if (this.grid.editSettings.newRowPosition === 'Bottom') {
        args.index = (this.grid.pageSettings.currentPage * this.grid.pageSettings.pageSize) - 1;
      }
    }
    else {
      this.validateUpdatePrimaryKey(args);
    }
  }

  private validateUpdatePrimaryKey(args: any) {
    if (this.editAcceptUpdatePrimaryKey) {
      this.editAcceptUpdatePrimaryKey = false;
      return;
    }
    if (args.primaryKey?.length > 0 && args.primaryKeyValue?.length > 0) {
      args.primaryKey.forEach((pk: string, index: number) => {
        var pkValue = args.primaryKeyValue[index];
        var pkValueData = args.data[pk];
        if (pkValueData != pkValue) {
          this.confirmUpdatePrimaryKey(args, pk, pkValue, pkValueData);
        }
      });
    }
  }

  private confirmUpdatePrimaryKey(args: any, pk: string, pkValue: any, pkValueData: any) {
    args.cancel = true; // Resubmit after confirmation
    var meta = this.meta.fieldMetaGet(pk);
    var msg: string = 'Nøglefelt ' + meta.text +
      ' opdateres fra ' + pkValue + ' til ' + pkValueData + '.' +
      ' Er du sikker?';
    this.ui.confirm(msg, 'Opdatering').then(ok => {
      if (ok) {
        this.editAcceptUpdatePrimaryKey = true;
        this.grid.endEdit();
      }
    });
  }

  private onSaveActionCompletePre(args: any) {
    this.resetEditOriginalData();
    this.grid.focusModule.restoreFocus();
  }

  private editInitPre(e: UiCommandEvent, isAdd: boolean) {
    this.editedData = e.rowData;
    this.uuid = uuidv4();

    this.fieldsVisibleAndAllowEditPrepare(isAdd);
    if (this.isModeDialog()) {
      if (!isAdd) {
        this.dialogInitFixArgsData(e);
      }
      if (this.design.useEditTemplate) {
        e.args.isCustomFormValidation = true; // Disable convertWidget method - rely on template for inputs
      }
      this.editTemplateService.setup(isAdd, this.grid.getColumns(), this.meta.tabMetadata, this.meta.fieldGroupMetadata, this.meta.fieldMetadata);
    }
  }


  private editInitPost(args: any, isAdd: boolean) {
    if (this.data.fieldMetadata) {
      this.fieldsVisibleAndAllowEditSetup(isAdd, this.isModeDialog());
    }
  }

  private dialogInitFixArgsData(e: UiCommandEvent) {
    var tr: HTMLTableRowElement = e.args?.row;
    if (tr == null) {
      return;
    }
    var uid: string = tr.getAttribute('data-uid');
    if (uid) {
      var rowObj = this.grid.getRowObjectFromUID(uid);
      e.rowData = rowObj;
      e.args.rowData = rowObj.data;
      e.args.foreignKeyData = rowObj.foreignKeyData;
    }
  }

  private title(data: any, isAdd: boolean): string {
    var title: string = this.data ? this.data.viewTextSingular(true) : "Post";
    this.data ? this.data.viewTextSingular(true) : "Post";
    if (isAdd) {
      title = 'Ny ' + title.toLowerCase();
    }
    else {
      if (data && this.data) {
        var dataTitle = this.data.viewDataTitle(data);
        title += dataTitle.length > 0 ? ': ' + dataTitle : '';
      }
    }
    return title;
  }

  private dialogInit(dialog: Dialog, data: any, isAdd: boolean) {
    if (this.isModeDialog() && dialog) {
      this.dialogHeaderInit(dialog, data, isAdd);
      if (!this.grid.enableAdaptiveUI && !(this.design?.editSettings?.dialog?.editInPane === true)) {
        dialog.enableResize = true;
        dialog.allowDragging = true;
      }
      dialog.closeOnEscape = false; // Remapped to uiCmdEditCancel
      this.dialogEventHandlerBind(dialog);
    }
  }

  private dialogHeaderInit(dialog: Dialog, data: any, isAdd: boolean) {
    var title: string = this.title(data, isAdd);
    if (dialog.header) {
      if (typeof dialog.header == 'string') {
        dialog.header = title;
      }
      else {
        var header: HTMLElement = <HTMLElement>dialog.header;
        if (header?.firstElementChild) {
          header.firstElementChild.innerHTML = title;
        }
        else {
          header.innerHTML = title;
        }
      }
    }
  }

  private fieldsVisibleAndAllowEditPrepare(isAdd: boolean) {
    this.dialogFieldsVisibleSave = [];
    this.dialogFieldsVisibleEdit = [];
    this.fieldsAllowEditSave = [];
    this.fieldsAllowEditEdit = [];

    this.data.fieldMetadata.forEach(meta => {
      if (meta.currentRowAllowEdit !== undefined) {
        meta.currentRowAllowEdit = undefined;
      }
      if (meta.currentRowVisible !== undefined) {
        meta.currentRowVisible = undefined;
      }
    });
  }

  private fieldsVisibleAndAllowEditSetup(isAdd: boolean, isDialog: boolean) {
    this.grid?.columns?.forEach((column: ColumnModel) => {
      if (column.field && this.data?.fieldMetadata) {
        var fieldMetadata: FieldMetadata = (column as any).meta ?? this.data.fieldMetadata.find(f => f.name == column.field);
        if (isDialog) {
          this.dialogFieldSetVisible(column, isAdd);
        }
        this.fieldSetAllowEdit(column, fieldMetadata, isAdd);
      }
    });

  }

  private dialogFieldSetVisible(c: ColumnModel, isAdd: boolean) {
    if (c.visible || c.visible == null) {
      this.dialogFieldsVisibleSave.push(c.field);
    }

    c.visible = this.meta.isFieldVisible(c, isAdd);

    if (c.visible !== false) {
      this.dialogFieldsVisibleEdit.push(c.field);  // Used in field group setup
    }
  }

  private fieldSetAllowEdit(c: ColumnModel, fieldMetadata: FieldMetadata, isAdd: Boolean) {
    if (c.allowEditing || c.allowEditing == null) {
      this.fieldsAllowEditSave.push(c.field);
    }

    if (fieldMetadata) {

      if (isAdd && fieldMetadata.allowEditOnAdd !== undefined) {
        c.allowEditing = fieldMetadata.allowEditOnAdd;
      }
      else if (fieldMetadata.allowEdit !== undefined) {
        c.allowEditing = fieldMetadata.allowEdit;
      };
      if (c.allowEditing && fieldMetadata.currentRowAllowEdit !== undefined) {
        c.allowEditing = fieldMetadata.currentRowAllowEdit;
      }
    }

    if (c.allowEditing || c.allowEditing == null) {
      this.fieldsAllowEditEdit.push(c.field);  // Used in field group setup
    }
  }

  private dialogFieldsResetVisible() {
    if (this.dialogFieldsVisibleSave && this.dialogFieldsVisibleSave.length > 0) {
      this.grid.columns.forEach((c: ColumnModel) => {
        if (c.field) {
          c.visible = (this.dialogFieldsVisibleSave.indexOf(c.field) >= 0);
        }
      });
      this.dialogFieldsVisibleSave = null;
    }
  }

  private fieldsResetAllowEdit() {
    if (this.fieldsAllowEditSave && this.fieldsAllowEditSave.length > 0) {
      this.grid.columns.forEach((c: ColumnModel) => {
        if (c.field) {
          c.allowEditing = (this.fieldsAllowEditSave.indexOf(c.field) >= 0);
        }
      });
      this.fieldsAllowEditSave = null;
    }
  }

  private isModeDialog(): boolean {
    return this.grid?.editSettings?.mode == "Dialog";
  }

  private fieldGroupSetup(dialog: Dialog) {
    if (this.isModeDialog() && this.meta.fieldGroupMetadata) {
      const content = dialog?.content as HTMLElement;
      const form = content?.querySelector('.e-gridform') as HTMLFormElement;
      if (form == null) {
        this.ui.error("Form kan ikke åbnes for redigering. Genopfrisk venligst browser", "Redigering");
        return;
      }
      const ej2 = (form as any).ej2_instances;
      const formEj2 = (ej2?.length > 0) ? ej2[0] : null;
      if (formEj2 == null) {
        this.ui.error("Form har ingen detaljer og kan ikke åbnes for redigering. Genopfrisk venligst browser", "Redigering");
        return;
      }

      const rules = formEj2.rules; // Save default valdation.
      formEj2.rules = {}; // Disable default valdation while layout changing

      this.dialogFieldGroupsSetup(dialog, form);

      formEj2.rules = rules; // Enable default valdation after layout changes
    }
  }

  /*********************
   * Keyboard shortcuts
   ********************/

  public onCmdEditCancel(event: UiCommandEvent): boolean {
    this.grid.editModule.closeEdit();
    return true;
  }


  private activeDialog: Dialog;
  private dialogEventHandlerBind(dialog: Dialog) {
    this.activeDialog = dialog;
    EventHandler.add(dialog.element, 'keydown', this.dialogKeyDown, this);
  };

  private dialogEventHandlerUnbind(dialog: Dialog) {
    EventHandler.remove(dialog.element, 'keydown', this.dialogKeyDown);
    this.activeDialog = null;
  }

  public dialogKeyDown(e: KeyboardEvent) {
    if (this.activeDialog) {
      //if ((this.activeDialog as any)?.isDisabled()) {
      //  return;
      //}
      var stop: boolean = false;
      this.dialogKeyboardShortcuts.forEach(shortcut => {
        if (this.command.keyboardShortcutMatch(e, shortcut)) {
          if (shortcut.cmd != uiCmdEditCancel || this.dialogValidateKeyboardCancel(e))
            var cancel: boolean = this.command.cmdDispatch(UiCommandSourceEnum.KeyboardShortcut, shortcut.cmd.id, e, null, UiActionTypeEnum.KeyboardShortcut, null);
          if (cancel || shortcut.allowsPreventDefault) {
            stop = true;
          }
        }
      });
      if (stop) {
        e.stopPropagation();
        e.preventDefault();
      }
    }
  }

  private dialogValidateKeyboardCancel(e: KeyboardEvent): boolean {
    var query = document.querySelector('.e-popup-open:not(.e-dialog)');
    // 'document.querySelector' is used to find the elements rendered based on body
    if ((query != null) && !query.classList.contains('e-toolbar-pop')) {
      return false;
    }
    else {
      (this.activeDialog as any).dlgClosedBy = 'escape';
      return true;
    }
  }

  /*
  private editDialogShortCutEvent(dialog: Dialog) {
{}    
  if (event.keyCode === 9) {
      if (this.isModal) {
          var buttonObj = void 0;
          if (!isNullOrUndefined(this.btnObj)) {
              buttonObj = this.btnObj[this.btnObj.length - 1];
          }
          if ((isNullOrUndefined(this.btnObj)) && (!isNullOrUndefined(this.ftrTemplateContent))) {
              buttonObj = this.getFocusElement(this.ftrTemplateContent);
          }
          if (isNullOrUndefined(this.btnObj) && isNullOrUndefined(this.ftrTemplateContent) && !isNullOrUndefined(this.contentEle)) {
              buttonObj = this.getFocusElement(this.contentEle);
          }
          if (!isNullOrUndefined(buttonObj) && document.activeElement === buttonObj.element && !event.shiftKey) {
              event.preventDefault();
              this.focusableElements(this.element).focus();
          }
          if (document.activeElement === this.focusableElements(this.element) && event.shiftKey) {
              event.preventDefault();
              if (!isNullOrUndefined(buttonObj)) {
                  buttonObj.element.focus();
              }
          }
      }
  }
  var element = document.activeElement;
  var isTagName = (['input', 'textarea'].indexOf(element.tagName.toLowerCase()) > -1);
  var isContentEdit = false;
  if (!isTagName) {
      isContentEdit = element.hasAttribute('contenteditable') && element.getAttribute('contenteditable') === 'true';
  }
  if (event.keyCode === 27 && this.closeOnEscape) {
      this.dlgClosedBy = DLG_ESCAPE_CLOSED;
      var query = document.querySelector('.e-popup-open:not(.e-dialog)');
      // 'document.querySelector' is used to find the elements rendered based on body
      if (!(!isNullOrUndefined(query) && !query.classList.contains('e-toolbar-pop'))) {
          this.hide(event);
      }
  }
*/


  /*
  diag if (event.keyCode === 27 && this.closeOnEscape) {
  this.dlgClosedBy = DLG_ESCAPE_CLOSED;
  var query = document.querySelector('.e-popup-open:not(.e-dialog)');
  // 'document.querySelector' is used to find the elements rendered based on body
  if (!(!isNullOrUndefined(query) && !query.classList.contains('e-toolbar-pop'))) {
      this.hide(event);
  }
  */

  private firstInputCtrl: HTMLInputElement = null;
  private dialogFieldGroupsSetup(dialog: Dialog, form: HTMLFormElement) {
    // By default all edit fields are in one table.
    // Change layout to field groups (and tabs if any)
    var table: HTMLTableElement = form.querySelector('.e-table');
    var tbody = table?.querySelector('tbody');
    if (tbody == null) {
      this.ui.error("Form har ingen indhold og kan ikke åbnes for redigering.", "Redigering");
      return;
    }

    this.firstInputCtrl = null;
    if (this.dialogFieldGroupsSetupTable(dialog, form, tbody)) {
      table.style.display = 'none';
      //form.removeChild(table); // Remove existing empty? table
    };
    if (this.firstInputCtrl != null) {
      this.focusInput(this.firstInputCtrl);
      this.firstInputCtrl = null;
    }
  }

  private focusInput(input: HTMLInputElement) {
    if (input) {
      this.getFocusElement(input)?.focus();
    }
  }

  private getFocusElement(input: HTMLInputElement): HTMLElement {
    if (input) {
      var ddlcontainer: HTMLElement = CerDropDownList.getDdlContainerDivFromInput(input);
      if (ddlcontainer) {
        return ddlcontainer;
      }
    }
    return input;
  }

  private dialogFieldGroupsSetupTable(dialog: Dialog, form: HTMLFormElement, tbodyStd: HTMLTableSectionElement): boolean {

    form.classList.add('e-form-card');
    if (form.parentElement != null) {
      form.parentElement.classList.add('e-form-card-container');
    }

    /*
    if (this.meta.tabMetadata) {
            var tab: HTMLDivElement = document.createElement('div');
            form.appendChild(tab);
            tab.classList.add('e-grid-field-tab');
            this.fieldTabMetadata.forEach(tabMeta => {
              var tabButton: HTMLButtonElement = document.createElement('button');
              tab.appendChild(tabButton);
              tabButton.classList.add('e-btn');
              tabButton.classList.add('e-btn-secondary');
              tabButton.classList.add('e-btn-small');
              tabButton.classList.add('e-btn-tab');
              tabButton.innerText = tabMeta.text;
              tabButton.addEventListener('click', (args) => {
               // this.dialogFieldGroupsFormTabClick(tabButton, tabMeta);
              });
            });
          }
    */

    var done: boolean = false;

    var i: number = 0;

    var tbodyFallback: HTMLTableSectionElement = null;
    var prevGroup: FieldGroupMetadata = null;
    var columnDiv: HTMLDivElement = null;
    var widthTotal: number = 100;
    var groups = this.meta.fieldGroupMetadata.filter(g => this.isFieldGroupVisible(g))
    groups.forEach(group => {
      var columnWidth: number = null;
      const colGroups = groups.filter(cg => cg.idx == group.idx);
      colGroups.forEach(cg => {
        if (cg.width != null && (columnWidth == null || cg.width > columnWidth)) {
          columnWidth = cg.width;
        }
      });
      columnWidth ??= 180;

      var newColumDiv: HTMLDivElement = this.dialogGroupSetupColumn(group, prevGroup, columnWidth, form);
      var newColumn: boolean = (newColumDiv != null);
      if (newColumn) {
        columnDiv = newColumDiv;
        widthTotal += columnWidth;
      }

      var tbody: HTMLTableSectionElement = this.dialogGroupSetupGroup(group, columnDiv, newColumn);
      tbodyFallback = tbody;

      group.fields.forEach(fieldName => {
        this.dialogGroupSetupField(fieldName, tbodyStd, tbody);
      });
      prevGroup = group;
      done = true;
    });

    if (tbodyFallback && tbodyStd.rows.length > 0) {
      for (i = 0; i < tbodyStd.rows.length; i++) {
        var row = tbodyStd.rows[i];
        tbodyFallback.appendChild(row); /* Move to fallback group (TODO: Required) */
      }
    }

    if (widthTotal > 0) {
      if (this.grid.editSettings.dialog?.params?.width != '100%' &&
        widthTotal > 330) {  // 330 = SF default
        dialog.width = (widthTotal + 100) + "px";
      }
    }
    return done;
  }

  private dialogGroupSetupColumn(group: FieldGroupMetadata, prevGroup: FieldGroupMetadata, columnWidth: number, form: HTMLFormElement): HTMLDivElement {
    var createNewColumn: boolean = ((prevGroup == null || prevGroup.idx != group.idx) && !this.grid.enableAdaptiveUI);
    if (createNewColumn) {
      var columnDiv: HTMLDivElement = document.createElement('div');
      columnDiv.classList.add('e-form-card-column');
      form.appendChild(columnDiv);
      columnDiv.style.flexBasis = columnWidth + "px";
      return columnDiv;
    }
    return null;
  }

  private dialogGroupSetupGroup(group: FieldGroupMetadata, columnDiv: HTMLDivElement, newColumn: boolean): HTMLTableSectionElement {
    var table: HTMLTableElement = document.createElement('table');
    table.classList.add('e-form-card-group');
    columnDiv.appendChild(table);
    var thead: HTMLTableSectionElement = document.createElement('thead');
    table.appendChild(thead);
    var row: HTMLTableRowElement = document.createElement('tr');
    thead.appendChild(row);
    var cell: HTMLTableCellElement = document.createElement('td');
    row.appendChild(cell);
    var groupTitle = document.createElement('div');
    groupTitle.innerText = group.text;
    groupTitle.classList.add('e-form-card-group-title');
    cell.appendChild(groupTitle);
    if (!newColumn) {
    }

    //var pre = document.createElement('pre');
    //h3.appendChild(pre);
    //pre.innerText = group.text;

    var tbody: HTMLTableSectionElement = document.createElement('tbody');
    table.appendChild(tbody);
    return tbody;
  }

  private dialogGroupSetupField(fieldName: string, tbodyStd: HTMLTableSectionElement, tbody: HTMLTableSectionElement) {
    var isVisible: boolean = (this.dialogFieldsVisibleEdit.indexOf(fieldName) >= 0);
    if (isVisible) {
      var input: HTMLInputElement = this.dialogTableInputFind(tbodyStd, fieldName);
      if (input) {
        this.dialogFieldGroupSetupFieldMove(input, tbody);
        if (!this.firstInputCtrl) {
          if (!this.inputIsDisabled(input)) {
            this.firstInputCtrl = input;
          }
        }
      }
    }
  }

  private dialogFieldGroupSetupFieldMove(input: HTMLInputElement, tbody: HTMLTableSectionElement) {
    var isRequired: boolean = input.required;
    if (isRequired) {
      input.required = false; // Ignore required validator
    }
    var td: HTMLTableCellElement = input.closest('td');
    td.style.width = '';

    var row: HTMLTableRowElement = input.closest('tr');
    if (row) {
      tbody.appendChild(row);
    }
    if (isRequired) {
      input.required = true;
    }
  }

  private isFieldGroupVisible(group: FieldGroupMetadata): boolean {
    var groupVisible: boolean = false;

    group?.fields?.forEach(name => {

      var field = this.fieldColumn(name);
      if (field && field.visible !== false) {
        groupVisible = true;
      }
    });

    return groupVisible;
  }

  public dialogTableInputFind(tbodyStd: HTMLElement, fieldName: string): HTMLInputElement {
    var id = '#' + this.grid.element.id + fieldName.replaceAll('.', '___');
    var input: HTMLInputElement = tbodyStd.querySelector(id) as HTMLInputElement;
    return input;
  }

  private batchEditInit() {
  }

  private batchEditToolbarClick(commandId: string) {
    const updateItemId = (<any>this.grid.toolbarModule).gridID + '_update';
    if (commandId == updateItemId) {
      this.batchEditSaveConfirmDialogSet();
      return;
    }
    //const editItemId = (<any>this.grid.toolbarModule).gridID + '_edit';
    //if (commandId == editItemId) {
    //  this.batchEditInit(e);
    //  return;
    //}
  }

  private batchEditSaveConfirmDialogSet() {
    if (this.grid.editSettings.showConfirmDialog == true) {
      if (this.design.editSettings.editBatchCancelConfirm &&
        !this.design.editSettings.editConfirm) {
        this.grid.editSettings.showConfirmDialog = false;  // Before save - will be reset after save
      }
    }
  }

  private batchEditSaveConfirmDialogReset() {
    if (this.grid.editSettings.showConfirmDialog == false) {
      if (this.design.editSettings.editBatchCancelConfirm &&
        !this.design.editSettings.editConfirm) {
        this.grid.editSettings.showConfirmDialog = true; // Reset after save
      }
    }
  }
}

function addRemoveClass(element: Element, cls: string, add: boolean) {
  if (add) {
    if (!element.classList.contains(cls)) {
      element.classList.add(cls);
    }
  }
  else {
    if (!element.classList.contains(cls)) {
      element.classList.add(cls);
    }
  }
}

function addRemoveActiveClasses(elements: HTMLCollectionOf<Element>, classes: string[], add: boolean) {
  for (var i = 0; i < elements.length; i++) {
    var element = elements[i];
    for (var j = 0; j < classes.length; j++) {
      addRemoveClass(element, classes[j], add);

      if (add) {
        element.setAttribute('aria-selected', 'true');
      }
      else {
        element.removeAttribute('aria-selected');
      }
    }
  }

}